import { Controller } from '@hotwired/stimulus'

const PAPPERS_SUGGESTIONS_URL = 'https://api.pappers.fr/v2/suggestions'
const RESULTS_LIMIT = 5

export default class extends Controller {
  static targets = ['name', 'results', 'registrationNumber', 'city', 'registrationDate']
  static values = { apiKey: String }

  timeout = null
  lastQuery = null

  connect() {
    this.handleClickOutside = this.#clickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  search() {
    const query = this.nameTarget.value.trim()

    if (query.length < 3) {
      this.#clearResults()
      return
    }

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      if (query === this.lastQuery) return
      this.lastQuery = query
      this.#fetchAndRender(query)
    }, 400)
  }

  select(event) {
    const item = event.currentTarget.dataset

    this.nameTarget.value = item.name

    if (this.hasRegistrationNumberTarget) {
      this.registrationNumberTarget.value = item.siret
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = item.city
    }

    if (this.hasRegistrationDateTarget) {
      this.#fillRegistrationDate(item.registrationDate)
    }

    this.#clearResults()
  }

  async #fetchAndRender(query) {
    const params = new URLSearchParams({
      cibles: 'nom_entreprise',
      q: query,
      longueur: RESULTS_LIMIT,
      api_token: this.apiKeyValue,
    })

    const response = await fetch(`${PAPPERS_SUGGESTIONS_URL}?${params}`)

    if (!response.ok) return

    const body = await response.json()
    this.#renderResults(this.#mapResults(body.resultats_nom_entreprise || []))
  }

  #mapResults(results) {
    return results.map(result => ({
      siret: result.siege?.siret ?? '',
      name: result.nom_entreprise ?? '',
      city: result.siege?.ville ?? '',
      registrationDate: result.date_creation ?? '',
    }))
  }

  #renderResults(results) {
    if (results.length === 0) {
      this.#clearResults()
      return
    }

    this.resultsTarget.innerHTML = results
      .map(result => `
        <li
          class='list-group-item list-group-item-action'
          data-action='click->legal-entity-lookup#select'
          data-siret='${result.siret}'
          data-name='${result.name}'
          data-city='${result.city}'
          data-registration-date='${result.registrationDate}'>
          <strong>${result.name}</strong><br>
          <small>${result.city}</small>
        </li>
      `)
      .join('')
  }

  #fillRegistrationDate(isoDate) {
    const formatted = this.#formatDate(isoDate)
    const flatpickrInstance = this.registrationDateTarget._flatpickr

    if (flatpickrInstance) {
      flatpickrInstance.setDate(formatted, true)
    } else {
      this.registrationDateTarget.value = formatted
    }
  }

  #formatDate(isoDate) {
    if (!isoDate) return ''

    const [year, month, day] = isoDate.split('-')
    if (!year || !month || !day) return isoDate

    return `${day}/${month}/${year}`
  }

  #clearResults() {
    this.resultsTarget.innerHTML = ''
  }

  #clickOutside(event) {
    const clickedInsideName = this.nameTarget.contains(event.target)
    const clickedInsideResults = this.resultsTarget.contains(event.target)

    if (!clickedInsideName && !clickedInsideResults) {
      this.#clearResults()
    }
  }
}
